import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveAssetUrl } from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import AddIcon from "@mui/icons-material/Add";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import TableActionCell from "components/table/TableActionCell";
import ClickableTableRow, { stopRowClickPropagation } from "components/table/ClickableTableRow";
import TableStatusChip from "components/table/TableStatusChip";
import ApprovalStatusChip from "components/table/ApprovalStatusChip";
import {
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton,
  InputAdornment, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
  MenuItem, Select, FormControl, InputLabel,
} from "@mui/material";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, SearchOutlined, CarOutlined,
} from "@ant-design/icons";
import StarIcon from "@mui/icons-material/Star";
import { brand } from "utils/designTokens";
import { useToast } from "contexts/ToastContext";

function OnlineDot({ online }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{
        width: 8, height: 8, borderRadius: "50%",
        bgcolor: online ? "#1f5b40" : "grey.400",
      }} />
      <Typography variant="caption" color={online ? "primary.dark" : "text.secondary"}>
        {online ? "Online" : "Offline"}
      </Typography>
    </Box>
  );
}

export default function DriverList() {
  const navigate = useNavigate();
  const { showToast, showApiError } = useToast();

  const [drivers,      setDrivers]      = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [delDialog,    setDelDialog]    = useState({ open: false, id: null, name: "" });
  const [deleting,     setDeleting]     = useState(false);
 
  const [togglingId,   setTogglingId]   = useState(null);

  useEffect(() => { loadDrivers(); }, []);

  const loadDrivers = async () => {
    setLoading(true);
    try {
      const res  = await api.get("/driver/list");
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setDrivers(data);
      setFiltered(data);
    } catch (err) {
      console.error("[DriverList] load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = search.toLowerCase().trim();
    let result = [...drivers];

    if (statusFilter !== "all")
      result = result.filter((d) => d.approval_status === statusFilter);

    if (q)
      result = result.filter((d) =>
        [d.display_name, d.user?.name, d.user?.phone, d.user?.username]
          .some((v) => v?.toLowerCase().includes(q))
      );

    setFiltered(result);
  }, [search, statusFilter, drivers]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/driver/delete/${delDialog.id}`);
      setDrivers((prev) => prev.filter((d) => d.id !== delDialog.id));
      setDelDialog({ open: false, id: null, name: "" });
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (driver) => {
    setTogglingId(driver.id);
    try {
      const res = await api.put(`/driver/status/${driver.id}`, {
        action: driver.status ? "inactive" : "active",  
        status: !driver.status,                         
      });

      const confirmed = res.data?.data?.status;
      setDrivers((prev) =>
        prev.map((d) =>
          d.id === driver.id
            ? { ...d, status: confirmed !== undefined ? confirmed : !d.status }
            : d
        )
      );
    } catch (err) {
      showApiError(err, "Status update failed");
    } finally {
      setTogglingId(null);
    }
  };

  const imgSrc = (driver) => resolveAssetUrl(driver?.profileImage?.imageUrl);

  return (
    <Box>
      <PageHeader
        title="Drivers"
        subtitle="Manage delivery drivers, approvals, and availability."
        count={filtered.length}
        actionLabel="Add Driver"
        actionIcon={AddIcon}
        onAction={() => navigate("/driver/create")}
      />

      {/* Filters */}
      <MainCard sx={{ mb: 2, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }} content={false}>
        <Box sx={{ p: 2.5, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search by name, phone, username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchOutlined /></InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
            sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="driver-approval-label">Approval</InputLabel>
            <Select
              labelId="driver-approval-label"
              value={statusFilter}
              label="Approval"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </MainCard>

      <MainCard contentSX={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && filtered.length === 0 && (
          <Box sx={{ textAlign: "center", p: 6 }}>
            <Typography variant="h6">No drivers found</Typography>
            <Button variant="contained" startIcon={<PlusOutlined />}
              sx={{ mt: 2 }} onClick={() => navigate("/driver/create")}>
              Add First Driver
            </Button>
          </Box>
        )}

        {!loading && filtered.length > 0 && (
          <TableContainer
            sx={{
              display: "block",
              maxHeight: { xs: "55vh", md: 520 },
              overflowX: "auto",
              overflowY: "auto",
              borderBottom: "none",
              "& .MuiTable-root": { borderBottom: "none" },
              "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
                borderBottom: "none"
              }
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Driver</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Approval</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Online</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((driver) => (
                  <ClickableTableRow
                    key={driver.id}
                    onClick={() => navigate(`/driver/view/${driver.id}`)}
                  >

                    {/* display_name + user.name + avatar */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar src={imgSrc(driver)}
                          sx={{ width: 44, height: 44, bgcolor: "primary.lighter" }}>
                          {(driver.display_name || "D")[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600} variant="body2">
                            {driver.display_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {driver.user?.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* user.phone + user.username */}
                    <TableCell>
                      <Typography variant="body2">{driver.user?.phone || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{driver.user?.username}
                      </Typography>
                    </TableCell>

                    {/* ── STATUS CHIP with loading state ── */}
                    <TableCell>
                      <Tooltip title="Click to toggle">
                        <TableStatusChip
                          active={!!driver.status}
                          loading={togglingId === driver.id}
                          onClick={stopRowClickPropagation(() => togglingId === null && handleToggleStatus(driver))}
                          sx={{ minWidth: 72 }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <ApprovalStatusChip status={driver.approval_status} />
                    </TableCell>

                    {/* rating */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.35}>
                        <StarIcon sx={{ fontSize: 16, color: brand.primary }} />
                        <Typography variant="body2" fontWeight={600}>
                          {Number(driver.rating?.current_rating || 0).toFixed(1)}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {driver.rating?.total_count || 0} reviews
                      </Typography>
                    </TableCell>

                    {/* is_online */}
                    <TableCell>
                      <OnlineDot online={driver.is_online} />
                    </TableCell>

                    {/* Actions */}
                    <TableActionCell>
                      <TableActions>
                        <TableActionButton
                          action="view"
                          title="View Details"
                          onClick={() => navigate(`/driver/view/${driver.id}`)}
                        />
                        <TableActionButton
                          action="edit"
                          title="Edit Driver"
                          onClick={() => navigate(`/driver/edit/${driver.id}`)}
                        />
                        <TableActionButton
                          action="delete"
                          title="Delete Driver"
                          onClick={() => setDelDialog({
                            open: true,
                            id: driver.id,
                            name: driver.display_name,
                          })}
                        />
                      </TableActions>
                    </TableActionCell>

                  </ClickableTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MainCard>

      {/* Delete dialog */}
      <Dialog open={delDialog.open}
        onClose={() => !deleting && setDelDialog({ open: false, id: null, name: "" })}>
        <DialogTitle>Delete Driver</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{delDialog.name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelDialog({ open: false, id: null, name: "" })}
            disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteOutlined />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}